import { useState, useCallback, useRef } from 'react';

const PREFIX = 'gas2:';

/**
 * Hook genérico para persistência de estado no localStorage.
 * Todas as chaves são prefixadas com "gas2:" para evitar colisões com outras libs.
 *
 * @param key      Chave sem prefixo (ex: "flex", "cnh")
 * @param initialValue  Valor inicial usado quando a chave não existe no storage
 * @returns [value, setValue, clearValue]
 */
function useLocalStorage<T>(
  key: string,
  initialValue: T,
): [T, (value: T | ((prev: T) => T)) => void, () => void] {
  const prefixedKey = PREFIX + key;
  // ref evita que initialValue em objetos literais entre em deps dos callbacks
  const initialRef = useRef(initialValue);

  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const raw = window.localStorage.getItem(prefixedKey);
      return raw !== null ? (JSON.parse(raw) as T) : initialRef.current;
    } catch {
      return initialRef.current;
    }
  });

  const setValue = useCallback(
    (value: T | ((prev: T) => T)) => {
      try {
        setStoredValue(prev => {
          const next =
            typeof value === 'function' ? (value as (p: T) => T)(prev) : value;
          window.localStorage.setItem(prefixedKey, JSON.stringify(next));
          return next;
        });
      } catch (err) {
        console.warn(`[useLocalStorage] Não foi possível salvar "${prefixedKey}"`, err);
      }
    },
    [prefixedKey],
  );

  const clearValue = useCallback(() => {
    try {
      window.localStorage.removeItem(prefixedKey);
      setStoredValue(initialRef.current);
    } catch (err) {
      console.warn(`[useLocalStorage] Não foi possível limpar "${prefixedKey}"`, err);
    }
  }, [prefixedKey]);

  return [storedValue, setValue, clearValue];
}

export default useLocalStorage;

